import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { DataTable, FilterBar, StatsCard, PaginationControls, SearchInput, LoadingState } from '../../components/admin/shared';
import { FileText, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import educentialApi from '../../api/educentialApi';

const Applications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });

  const [filters, setFilters] = useState({
    feature: searchParams.get('feature') || '',
    status: searchParams.get('status') || '',
    country: searchParams.get('country') || '',
    search: ''
  });

  const features = [
    { value: 'overseas', label: 'Overseas Education' },
    { value: 'career', label: 'Career Counseling' },
    { value: 'skills', label: 'Skill Development' },
    { value: 'test-prep', label: 'Test Preparation' },
    { value: 'scholarships', label: 'Scholarship Finder' },
    { value: 'visa', label: 'Visa Assistance' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'pre-departure', label: 'Pre-Departure' },
    { value: 'post-arrival', label: 'Post-Arrival' },
    { value: 'alumni', label: 'Alumni Network' }
  ];

  useEffect(() => {
    loadApplications();
  }, [filters, pagination.page, pagination.limit]);

  const loadApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.feature) params.append('feature', filters.feature);
      if (filters.status) params.append('status', filters.status);
      if (filters.country) params.append('country', filters.country);
      if (filters.search) params.append('search', filters.search);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await educentialApi.get(`/api/admin/applications?${params.toString()}`);
      setApplications(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));

      // Load stats
      const statsResponse = await educentialApi.get('/api/admin/stats');
      setStats(statsResponse.data);
    } catch (err) {
      console.error('Error loading applications:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load applications';
      setError(errorMessage);

      // If unauthorized, redirect to login
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const handleClearFilters = () => {
    setFilters({ feature: '', status: '', country: '', search: '' });
    setSearchParams({});
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-800',
      SUBMITTED: 'bg-blue-100 text-blue-800',
      UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
      OFFER_SECURED: 'bg-green-100 text-green-800',
      VISA_IN_PROGRESS: 'bg-purple-100 text-purple-800',
      COMPLETED: 'bg-teal-100 text-teal-800',
      REJECTED: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const getFeatureLabel = (feature) => {
    return features.find(f => f.value === feature)?.label || feature;
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (app) => <span className="font-mono text-xs">#{app.id}</span>
    },
    {
      key: 'feature',
      label: 'Feature',
      render: (app) => (
        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
          {getFeatureLabel(app.service || 'overseas')}
        </span>
      )
    },
    {
      key: 'student',
      label: 'Student',
      render: (app) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{app.studentName || 'N/A'}</div>
          <div className="text-xs text-gray-500">{app.userEmail}</div>
        </div>
      )
    },
    {
      key: 'country',
      label: 'Country',
      render: (app) => <span className="text-sm">{app.country || 'N/A'}</span>
    },
    {
      key: 'status',
      label: 'Status',
      render: (app) => getStatusBadge(app.status)
    },
    {
      key: 'createdAt',
      label: 'Submitted',
      render: (app) => (
        <span className="text-sm text-gray-600">
          {new Date(app.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  const filterConfig = [
    {
      name: 'feature',
      label: 'Feature',
      type: 'select',
      value: filters.feature,
      options: features
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filters.status,
      options: [
        { value: 'SUBMITTED', label: 'Submitted' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
        { value: 'OFFER_SECURED', label: 'Offer Secured' },
        { value: 'VISA_IN_PROGRESS', label: 'Visa in Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'REJECTED', label: 'Rejected' }
      ]
    },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      value: filters.country,
      options: [
        { value: 'UK', label: 'United Kingdom' },
        { value: 'US', label: 'United States' },
        { value: 'Canada', label: 'Canada' },
        { value: 'Australia', label: 'Australia' },
        { value: 'Germany', label: 'Germany' },
        { value: 'France', label: 'France' }
      ]
    }
  ];

  if (loading && applications.length === 0) {
    return (
      <AdminLayout title="Applications">
        <LoadingState message="Loading applications..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Applications">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Applications Management</h2>
            <p className="text-gray-600 mt-1">View and manage all applications across all features</p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error Loading Applications</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
              <button
                onClick={() => loadApplications()}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard
            title="Total Applications"
            value={stats?.applications?.total || 0}
            icon={FileText}
          />
          <StatsCard
            title="Under Review"
            value={stats?.applications?.byStatus?.UNDER_REVIEW || 0}
            icon={Clock}
          />
          <StatsCard
            title="Completed"
            value={stats?.applications?.byStatus?.COMPLETED || 0}
            icon={CheckCircle}
          />
          <StatsCard
            title="Rejected"
            value={stats?.applications?.byStatus?.REJECTED || 0}
            icon={XCircle}
          />
        </div>

        {/* Search */}
        <SearchInput
          value={filters.search}
          onChange={(value) => handleFilterChange('search', value)}
          placeholder="Search by student name or email..."
        />

        {/* Filters */}
        <FilterBar
          filters={filterConfig}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Applications Table */}
        <DataTable
          columns={columns}
          data={applications}
          loading={loading}
          emptyMessage="No applications found. Try adjusting your filters."
          onRowClick={(app) => navigate(`/admin/applications/${app.id}`)}
        />

        {/* Pagination */}
        <PaginationControls
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.limit)}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onItemsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        />
      </div>
    </AdminLayout>
  );
};

export default Applications;
